import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { toast } from 'sonner';
import { api, ApiCallError } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Field, FieldDescription, FieldGroup, FieldLabel } from '@/components/ui/field';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { AppClientPicker } from '@/components/app-client-picker';
import type { ClientRow } from '@/components/app-client-form-dialog';

interface ItemRow {
  description: string;
  quantity: string;
  unitPrice: string;
  taxRate: '10' | '5' | '0';
}

const CURRENCIES = ['PYG', 'USD', 'ARS', 'EUR', 'BRL'] as const;
const PAYMENT_METHODS: Array<{ value: string; label: string }> = [
  { value: 'efectivo', label: 'Efectivo' },
  { value: 'transferencia', label: 'Transferencia' },
  { value: 'giro', label: 'Giro' },
  { value: 'billetera', label: 'Billetera electrónica' },
  { value: 'tarjeta_empresarial', label: 'Tarjeta empresarial' },
  { value: 'vale', label: 'Vale' },
  { value: 'retencion', label: 'Retención' },
];

const errMsg = (e: unknown, fallback: string) =>
  e instanceof ApiCallError ? (e.body.message ?? e.body.error) : fallback;

/** Form one-shot para emitir una factura electrónica desde el panel tenant. */
export function ElectronicInvoiceFormDialog({
  open,
  onClose,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [client, setClient] = useState<ClientRow | null>(null);
  const [currency, setCurrency] = useState<(typeof CURRENCIES)[number]>('PYG');
  const [paymentMethod, setPaymentMethod] = useState<string>('efectivo');
  const [saleType, setSaleType] = useState<'contado' | 'credito'>('contado');
  const [rows, setRows] = useState<ItemRow[]>([
    { description: '', quantity: '1', unitPrice: '', taxRate: '10' },
  ]);
  const qc = useQueryClient();

  const clientName = client ? client.razonSocial || client.name : '';
  const clientRuc = client?.ruc ?? null;

  function reset() {
    setClient(null);
    setCurrency('PYG');
    setPaymentMethod('efectivo');
    setSaleType('contado');
    setRows([{ description: '', quantity: '1', unitPrice: '', taxRate: '10' }]);
  }

  const create = useMutation({
    mutationFn: () => {
      const items = rows
        .filter((r) => r.description.trim() && r.unitPrice.trim())
        .map((r) => ({
          description: r.description.trim(),
          quantity: Number(r.quantity) || 1,
          unitPrice: Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0,
          taxRate: Number(r.taxRate),
        }));
      return api('/api/app/electronic-invoices', {
        method: 'POST',
        body: JSON.stringify({
          clientId: client?.id ?? null,
          clientName: clientName.trim() || 'Sin nombre',
          clientRuc,
          currency,
          paymentMethod,
          saleType,
          items,
        }),
      });
    },
    onSuccess: () => {
      toast.success('Factura electrónica emitida.');
      qc.invalidateQueries({ queryKey: ['app-electronic-invoices'] });
      reset();
      onSaved();
    },
    onError: (e) => toast.error(errMsg(e, 'No se pudo emitir la factura')),
  });

  // IVA INCLUIDO: el precio dictado ES el total. El total a pagar = suma de líneas.
  const total = rows.reduce(
    (s, r) => s + (Number(r.quantity) || 0) * (Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0),
    0
  );
  // A crédito el cliente es obligatorio y debe tener RUC (la SET no permite
  // crédito a innominado). A contado el cliente es opcional.
  const valid =
    rows.some((r) => r.description.trim() && r.unitPrice.trim()) &&
    (saleType === 'contado' || (!!client && !!client.ruc));

  const setRow = (i: number, k: keyof ItemRow, v: string) =>
    setRows((rs) => rs.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));

  const fmtMoney = (n: number) =>
    currency === 'PYG'
      ? `Gs ${Math.round(n).toLocaleString('es-PY')}`
      : `${currency} ${Math.round(n).toLocaleString('es-PY')}`;

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (!o) onClose();
      }}
    >
      <DialogContent className="max-h-[calc(100dvh-2rem)] w-[95vw] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Nueva factura electrónica</DialogTitle>
          <DialogDescription>
            El precio es IVA incluido. La factura se emite por SIFEN (modo demo por ahora).
          </DialogDescription>
        </DialogHeader>

        <FieldGroup>
          {/* Cliente */}
          <Field>
            <FieldLabel required={saleType === 'credito'}>Cliente</FieldLabel>
            <AppClientPicker
              value={client?.id ?? null}
              selectedLabel={client ? client.razonSocial || client.name : undefined}
              onChange={setClient}
            />
            {saleType === 'credito' && client && !client.ruc && (
              <FieldDescription className="text-destructive">
                A crédito el cliente debe tener RUC cargado.
              </FieldDescription>
            )}
          </Field>

          {/* Condición / Moneda / Medio de pago */}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field>
              <FieldLabel htmlFor="ei-condicion">Condición</FieldLabel>
              <Select value={saleType} onValueChange={(v) => setSaleType(v as typeof saleType)}>
                <SelectTrigger id="ei-condicion">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="contado">Contado</SelectItem>
                  <SelectItem value="credito">Crédito</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="ei-moneda">Moneda</FieldLabel>
              <Select value={currency} onValueChange={(v) => setCurrency(v as typeof currency)}>
                <SelectTrigger id="ei-moneda">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {CURRENCIES.map((c) => (
                    <SelectItem key={c} value={c}>
                      {c}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field>
              <FieldLabel htmlFor="ei-pago">Medio de pago</FieldLabel>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger id="ei-pago">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {PAYMENT_METHODS.map((m) => (
                    <SelectItem key={m.value} value={m.value}>
                      {m.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
          </div>

          {/* Ítems */}
          <Field>
            <div className="flex items-center justify-between">
              <FieldLabel>Ítems</FieldLabel>
              <span className="text-xs text-muted-foreground">Precio IVA incluido</span>
            </div>
            <div className="space-y-2">
              {rows.map((r, i) => {
                const lineTotal =
                  (Number(r.quantity) || 0) * (Number(r.unitPrice.replace(/[^\d.-]/g, '')) || 0);
                return (
                  <div key={i} className="space-y-2 rounded-lg border border-border bg-muted/20 p-3">
                    <div className="flex items-start gap-2">
                      <Input
                        value={r.description}
                        onChange={(e) => setRow(i, 'description', e.target.value)}
                        placeholder="Descripción (ej: Servicio de consultoría)"
                        className="flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="icon"
                        className="shrink-0 text-muted-foreground"
                        disabled={rows.length === 1}
                        onClick={() => setRows((rs) => rs.filter((_, idx) => idx !== i))}
                        title="Quitar ítem"
                      >
                        <X className="size-4" />
                      </Button>
                    </div>
                    <div className="flex flex-wrap items-end gap-2">
                      <div className="w-16 space-y-1">
                        <span className="text-[11px] text-muted-foreground">Cant.</span>
                        <Input
                          inputMode="numeric"
                          value={r.quantity}
                          onChange={(e) => setRow(i, 'quantity', e.target.value)}
                        />
                      </div>
                      <div className="w-32 space-y-1">
                        <span className="text-[11px] text-muted-foreground">Precio</span>
                        <Input
                          inputMode="numeric"
                          value={r.unitPrice}
                          onChange={(e) => setRow(i, 'unitPrice', e.target.value)}
                          placeholder="55000"
                        />
                      </div>
                      <div className="w-28 space-y-1">
                        <span className="text-[11px] text-muted-foreground">IVA</span>
                        <Select value={r.taxRate} onValueChange={(v) => setRow(i, 'taxRate', v)}>
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="10">10%</SelectItem>
                            <SelectItem value="5">5%</SelectItem>
                            <SelectItem value="0">Exenta</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="ml-auto space-y-1 text-right">
                        <span className="block text-[11px] text-muted-foreground">Subtotal</span>
                        <span className="text-sm font-medium tabular-nums">
                          {lineTotal ? fmtMoney(lineTotal) : '—'}
                        </span>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
            <Button
              variant="outline"
              size="sm"
              className="w-fit"
              onClick={() =>
                setRows((rs) => [...rs, { description: '', quantity: '1', unitPrice: '', taxRate: '10' }])
              }
            >
              <Plus className="mr-2 size-4" /> Agregar ítem
            </Button>
          </Field>

          {/* Total */}
          <div className="flex items-center justify-between rounded-lg bg-muted px-4 py-3">
            <span className="text-sm font-medium text-muted-foreground">
              Total a {saleType === 'credito' ? 'cobrar' : 'pagar'}
            </span>
            <span className="text-xl font-bold tabular-nums">{fmtMoney(total)}</span>
          </div>
        </FieldGroup>

        <DialogFooter>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button disabled={!valid || create.isPending} onClick={() => create.mutate()}>
            Emitir factura
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
